import { useEffect, useRef, lazy, Suspense } from "react";
import { useParams, Navigate } from "react-router-dom";
import { Header } from "@/components/landing/Header";
import { Marquee } from "@/components/landing/Marquee";
import { SourcingBand } from "@/components/landing/SourcingBand";
import { STATE_BY_SLUG } from "@/config/states";
import { INTAKE_URLS, LAUNCHED } from "@/config/intake";
import { ArrowRight, Bell, Check, Info, MapPin } from "lucide-react";
import { motion } from "framer-motion";
import { WaitlistInline } from "@/components/landing/WaitlistInline";
import { trackStatePageView } from "@/lib/analytics";

const Categories = lazy(() => import("@/components/landing/Categories").then(m => ({ default: m.Categories })));
const HowItWorks = lazy(() => import("@/components/landing/HowItWorks").then(m => ({ default: m.HowItWorks })));
const MeetClinicians = lazy(() => import("@/components/landing/MeetClinicians").then(m => ({ default: m.MeetClinicians })));
const Pricing = lazy(() => import("@/components/landing/Pricing").then(m => ({ default: m.Pricing })));
const FAQ = lazy(() => import("@/components/landing/FAQ").then(m => ({ default: m.FAQ })));
const FinalCTA = lazy(() => import("@/components/landing/FinalCTA").then(m => ({ default: m.FinalCTA })));
const Footer = lazy(() => import("@/components/landing/Footer").then(m => ({ default: m.Footer })));
const TrustBar = lazy(() => import("@/components/landing/TrustBar").then(m => ({ default: m.TrustBar })));

const State = () => {
  const { state: slug } = useParams<{ state: string }>();
  const state = slug ? STATE_BY_SLUG[slug] : undefined;
  const firedFor = useRef<string | null>(null);

  // Fire state_page_view once per (state) per mount. Keyed by code so that
  // navigating between /states/* in the same session re-fires for each new state.
  useEffect(() => {
    if (!state) return;
    if (firedFor.current === state.code) return;
    firedFor.current = state.code;
    trackStatePageView({ code: state.code, name: state.name, slug: state.slug });
  }, [state]);

  useEffect(() => {
    if (!state) return;
    const title = `Olive Health — Telehealth membership in ${state.name}`;
    const description = `Olive Health is now ${LAUNCHED ? "available" : "coming soon"} in ${state.name}. GLP-1, peptides, hormones, and longevity protocols from US-licensed clinicians, shipped to your door.`;
    document.title = title;

    const setMeta = (selector: string, attr: string, key: string, content: string) => {
      let el = document.querySelector(selector) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta(`meta[name="description"]`, "name", "description", description);
    setMeta(`meta[property="og:title"]`, "property", "og:title", title);
    setMeta(`meta[property="og:description"]`, "property", "og:description", description);
    setMeta(`meta[name="twitter:title"]`, "name", "twitter:title", title);
    setMeta(`meta[name="twitter:description"]`, "name", "twitter:description", description);

    const ldId = `ld-state-${state.code}`;
    const ldFaqId = `ld-faq-state-${state.code}`;
    document.getElementById(ldId)?.remove();
    document.getElementById(ldFaqId)?.remove();
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.id = ldId;
    s.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "MedicalBusiness",
      name: `Olive Health — ${state.name}`,
      areaServed: { "@type": "State", name: state.name },
      medicalSpecialty: ["PrimaryCare", "Endocrine"],
    });
    document.head.appendChild(s);

    import("@/components/landing/FAQ").then(({ FAQS }) => {
      const fs = document.createElement("script");
      fs.type = "application/ld+json";
      fs.id = ldFaqId;
      fs.text = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQS.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      });
      document.head.appendChild(fs);
    });
    return () => {
      document.getElementById(ldId)?.remove();
      document.getElementById(ldFaqId)?.remove();
    };
  }, [state]);

  if (!state) return <Navigate to="/" replace />;

  const intakeHref = `${INTAKE_URLS.default}?state=${state.code}`;

  const metroList = state.metros.slice(0, 3).join(", ");
  const lastMetro = state.metros[state.metros.length - 1];
  const metroSentence = state.metros.length > 1
    ? `${state.metros.slice(0, -1).join(", ")} and ${lastMetro}`
    : state.metros[0];

  return (
    <main className="relative min-h-screen bg-background">
      <Header />

      <section className="relative pt-28 md:pt-36 pb-16 md:pb-24 bg-gradient-warm border-b border-border">
        <div className="container-wide max-w-3xl">
          <p className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-olive font-medium">
            <MapPin className="size-3.5" aria-hidden /> {state.name} · serving {metroList}
          </p>
          <h1 className="font-display text-[clamp(2.4rem,5.5vw,4.5rem)] leading-[1.02] mt-5 text-foreground text-balance">
            Telehealth membership for {state.name}.
          </h1>
          <p className="mt-6 text-lg text-foreground/70 leading-relaxed max-w-xl">
            One membership covers GLP-1, peptides, hormones, longevity and labs — prescribed
            by clinicians licensed in {state.name} and shipped to members in {metroSentence}.
          </p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mt-8 inline-flex flex-wrap items-center gap-3"
          >
            {LAUNCHED ? (
              <>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-olive/12 text-olive text-xs px-3 py-1.5 font-medium">
                  <Check className="size-3.5" strokeWidth={2.5} /> Live in {state.name}
                </span>
                {state.pharmacy && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary text-foreground/75 text-xs px-3 py-1.5 font-medium">
                    Compounded in-state · faster shipping
                  </span>
                )}
                <a
                  href={intakeHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-7 py-4 text-sm font-medium hover:opacity-90 transition"
                >
                  Start membership <ArrowRight className="size-4" />
                </a>
              </>
            ) : (
              <div className="w-full max-w-lg">
                <div className="inline-flex items-center gap-1.5 rounded-full bg-accent/12 text-accent text-xs px-3 py-1.5 font-medium mb-4">
                  <Bell className="size-3.5" />
                  Launching soon in {state.name}
                </div>
                <p className="text-sm text-foreground/70 mb-4 leading-relaxed">
                  {state.code === "PR"
                    ? `Olive will serve Puerto Rico from our mainland 503A pharmacies. Drop your email and we'll let you know the moment we go live.`
                    : `${state.name} is on our launch list. Drop your email and we'll let you know the moment we go live.`}
                </p>
                {state.code === "PR" && (
                  <p className="inline-flex items-start gap-1.5 text-xs text-foreground/65 bg-accent/8 rounded-lg px-3 py-2 mb-4">
                    <Info className="size-3.5 text-accent mt-0.5 shrink-0" />
                    Puerto Rico orders typically arrive in 5–7 business days.
                  </p>
                )}
                <WaitlistInline forceState={state.code} placement={`state_hero_${state.code}`} />
              </div>
            )}
          </motion.div>

          <dl className="mt-12 grid grid-cols-3 gap-6 pt-8 border-t border-border/60 max-w-xl">
            <div>
              <dt className="text-[10px] uppercase tracking-[0.2em] text-foreground/65 font-medium">
                Ships to
              </dt>
              <dd className="font-display text-2xl text-foreground mt-2 leading-none">
                {state.metros.length}<span className="text-olive">+</span>
                <span className="text-xs text-foreground/70 ml-1.5 font-sans">metros</span>
              </dd>
            </div>
            <div>
              <dt className="text-[10px] uppercase tracking-[0.2em] text-foreground/65 font-medium">
                Compounded at
              </dt>
              <dd className="font-display text-2xl text-foreground mt-2 leading-none">
                503<span className="text-olive">A</span>
                <span className="text-xs text-foreground/70 ml-1.5 font-sans">FDA-reg</span>
              </dd>
            </div>
            <div>
              <dt className="text-[10px] uppercase tracking-[0.2em] text-foreground/65 font-medium">
                Avg. arrival
              </dt>
              <dd className="font-display text-2xl text-foreground mt-2 leading-none">
                {state.code === "PR" ? "5–7" : state.pharmacy ? "2–3" : "3–5"}<span className="text-olive">d</span>
                <span className="text-xs text-foreground/70 ml-1.5 font-sans">free ship</span>
              </dd>
            </div>
          </dl>
        </div>
      </section>

      <Marquee />
      <Suspense fallback={null}>
        <SourcingBand />
        <Categories />
        <HowItWorks />
        <MeetClinicians />
        <Pricing />
        <FAQ />
        <FinalCTA />
        <TrustBar />
        <Footer />
      </Suspense>
    </main>
  );
};

export default State;
